// ============================================================================
// NOTICE TEMPLATE PREVIEW MODAL — read-only (MSTR-NTMP-001)
// ============================================================================
// Large modal. Renders body_html inside a sandboxed <iframe srcDoc> so the
// template's styles/scripts cannot bleed into the app shell. Placeholder
// tokens (e.g. {{borrower_name}}) are shown verbatim — this preview is for
// structural review, not merge testing.

import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { NoticeTemplate } from 'api/types/noticeTemplate.types';

export interface NoticeTemplatePreviewModalProps {
  isOpen: boolean;
  record: NoticeTemplate | null;
  onClose: () => void;
}

const categoryLabel = (c: string): string => c.replace(/_/g, ' ');

const NoticeTemplatePreviewModal: React.FC<NoticeTemplatePreviewModalProps> = ({
  isOpen, record, onClose,
}) => {
  const srcDoc = useMemo(() => {
    if (!record) return '';
    const body = record.bodyHtml || '<p style="color:#6b7280;">(empty)</p>';
    // Minimal wrapper so the body inherits readable typography without
    // inheriting the app's CSS variables. Scripts are blocked by the
    // iframe `sandbox` attribute.
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      body {
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        line-height: 1.55;
        color: #111827;
        background: #ffffff;
        padding: 1.25rem 1.5rem;
        margin: 0;
      }
      p { margin: 0 0 0.75rem 0; }
      b, strong { font-weight: 600; }
      code { background: #f3f4f6; padding: 0 3px; border-radius: 3px; font-size: 0.9em; }
      h1, h2, h3 { margin-top: 0; }
    </style>
  </head>
  <body>${body}</body>
</html>`;
  }, [record]);

  if (!isOpen || !record) return null;

  const MetaItem: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
        {children}
      </span>
    </div>
  );

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            <IconSystem.eye size={18} /> &nbsp;Preview — {record.templateCode}
          </h2>
          <button className="modal-close" onClick={onClose} title="Close">
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Metadata strip */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '0.75rem 1.25rem',
              padding: '0.75rem 1rem',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
            }}
          >
            <MetaItem label="Name">{record.templateName}</MetaItem>
            <MetaItem label="Category">{categoryLabel(record.applicableCategory)}</MetaItem>
            <MetaItem label="Language">{record.languageCode}</MetaItem>
            <MetaItem label="Notice Type">
              {record.noticeTypeName || '-'}{record.noticeTypeCode ? ` (${record.noticeTypeCode})` : ''}
            </MetaItem>
            <MetaItem label="Action Type">
              {record.actionTypeName
                ? `${record.actionTypeName}${record.actionTypeCode ? ` (${record.actionTypeCode})` : ''}`
                : '—'}
            </MetaItem>
            <MetaItem label="Status">{record.statusCode || 'ACTIVE'}</MetaItem>
          </div>

          {record.subject && (
            <div style={{
              padding: '0.5rem 0.75rem',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '0.9375rem',
              fontWeight: 500,
            }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Subject
              </span>
              <div style={{ marginTop: '2px' }}>{record.subject}</div>
            </div>
          )}

          {/* Rendered body in sandboxed iframe */}
          <div style={{ flex: 1, minHeight: '360px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Rendered Body
            </div>
            <iframe
              title={`Notice preview — ${record.templateCode}`}
              srcDoc={srcDoc}
              sandbox=""
              style={{
                width: '100%',
                flex: 1,
                minHeight: '360px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                background: '#ffffff',
              }}
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.375rem' }}>
              <code>{'{{placeholders}}'}</code> are shown verbatim. Merge happens at notice generation time.
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default NoticeTemplatePreviewModal;
